import { SignInWithApple, SignInWithAppleResponse, SignInWithAppleOptions } from '@capacitor-community/apple-sign-in';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { isPlatform } from '@ionic/vue';
import { FacebookLogin, FacebookLimitedLoginResponse, FacebookLoginResponse } from '@whiteguru/capacitor-plugin-facebook-login';
import { AppTrackingTransparency, AppTrackingStatusResponse } from 'capacitor-plugin-app-tracking-transparency';
import authApi from '@/api/authApi';
import type { SocialProvider } from '@/shared/interfaces/user';
import { TenantStyles } from '@/shared/interfaces/tenant';
import useToast from '@/shared/composables/useToast';
import useStorageService from '@/shared/composables/useStorageService';

const centralUrl = window.centralUrl;
const { presentToast } = useToast();
import nativeAppConfig from '@/config/native-app.config.json';
const { get, set } = useStorageService();

const requestPermission = async (): Promise<AppTrackingStatusResponse> => {
	const response = await AppTrackingTransparency.requestPermission();
	return response;
};

const handleNativeSocialLogin = async (type: SocialProvider = 'facebook', t: any): Promise<string | null | undefined> => {
	try {
		switch (type) {
			case 'facebook':
				{
					if (isPlatform('ios')) {
						const permissions = await requestPermission();
						if (permissions.status && permissions.status === 'authorized') {
							return new Promise((resolve, reject) => {
								setTimeout(async () => {
									const signInWithFacebook = await _signInWithFacebook();
									return resolve(signInWithFacebook);
								}, 2000);
							});
						} else {
							return new Promise((resolve, reject) => {
								setTimeout(async () => {
									const signInWithFacebookWithLimitedTracking = await _signInWithFacebookWithLimitedTracking();
									return resolve(signInWithFacebookWithLimitedTracking);
								}, 2000);
							});
						}
					} else {
						return await _signInWithFacebook();
					}
				}
				break;
			case 'google': {
				return await _signInWithGoogle();
			}
			case 'apple': {
				return await _signInWithApple();
			}
		}
	} catch (e) {
		await presentToast('top', t('auth.sign_in_email_page.errors.other'), 5000, 'danger');
	}
};

const _signInWithFacebookWithLimitedTracking = async () => {
	const result: FacebookLimitedLoginResponse = await FacebookLogin.loginWithLimitedTracking({
		permissions: ['email', 'public_profile'],
	});
	if (result && result.authenticationToken) {
		return await _nativeAppLogin('facebook', result.authenticationToken.token);
	} else {
		return null;
	}
};

const _signInWithFacebook = async () => {
	const result: FacebookLoginResponse = await FacebookLogin.login({
		permissions: ['email', 'public_profile'],
	});

	if (result?.accessToken) {
		return await _nativeAppLogin('facebook', result.accessToken.token, null, null, null);
	} else {
		// this.utilsService.showCommonError('Something went wrong while authenticating with Facebook');
	}
};

const _signInWithGoogle = async () => {
	const { authentication } = await GoogleAuth.signIn();
	if (authentication.accessToken) {
		return await _nativeAppLogin('google', authentication.accessToken);
	} else {
		return null;
		// this.utilsService.showCommonError('Something went wrong while authenticating with Facebook');
	}
};

const _signInWithApple = async () => {
	const options: SignInWithAppleOptions = {
		clientId: nativeAppConfig.APPLE_CLIENT_ID,
		redirectURI: nativeAppConfig.APPLE_REDIRECT_URL,
		scopes: 'email, name',
		state: '12345',
		nonce: 'nonce',
	};

	return await SignInWithApple.authorize(options)
		.then(async (result: SignInWithAppleResponse) => {
			// Handle user information
			// Validate token with server and create new session
			const nativeLogin = await _nativeAppLogin('apple', result.response?.identityToken, null, result.response?.givenName, result?.response?.familyName);
			if (nativeLogin) {
				logLoginHistory('apple');
			}
			return nativeLogin;
		})
		.catch((error) => {
			// Handle error
			return null;
		});
};

const _nativeAppLogin = async (type: SocialProvider, accessToken: string, secret?: string | null, firstname?: string | null, lastname?: string | null) => {
	try {
		const response = await _nativeSocialAppLogin(type, accessToken, secret, firstname, lastname);
		if (response?.accessToken) {
			return response.accessToken;
		}
	} catch (e) {
		// this.utilsService.showCommonApiError(e);
		return null;
	}
};

const _nativeSocialAppLogin = async (provider: SocialProvider, token: string, secret?: string | null, firstname?: string | null, lastname?: string | null): Promise<{ accessToken: string }> => {
	const url = `/auth/social/app/${provider}`;

	let body: { token: string; secret?: string; firstname?: string; lastname?: string } = { token };

	if (secret) {
		body = { ...body, secret };
	}

	if (firstname) {
		body = { ...body, firstname };
	}

	if (lastname) {
		body = { ...body, lastname };
	}

	const { data } = await authApi.post<{ accessToken: string }>(url, body);

	return data;
};

const handleWebSocialLogin = async (type: SocialProvider = 'facebook', tenantStyles: TenantStyles) => {
	// if (type === 'google' && this.browserService.isEmbeddedBrowser) {
	//   const alert = await this.alertController.create({
	//     subHeader: 'Your browser does not support Google Login',
	//     message: 'Please open this app in <b>Chrome</b> or <b>Safari</b>',
	//     buttons: [{ text: 'OK' }],
	//   });

	//   await alert.present();
	//   return;
	// }

	const redirectUrl = window.encodeURIComponent(window.location.href) + '?loginMethod=' + type;
	const failedUrl = window.encodeURIComponent(window.location.href) + '?failed=true&loginMethod=' + type;

	location.href = `${centralUrl}/auth/social/${type}/${tenantStyles.id}?redirect_url=${redirectUrl}&failed_url=${failedUrl}`;
};

const logLoginHistory = async (provider: string) => {
	try {
		const timestamp = new Date().toISOString();

		const logData = { provider, timestamp };
		await set('previousLoginMethod', JSON.stringify(logData));
		console.info('Login history logged:', logData);
	} catch (error) {
		console.error('Error in logLoginHistory:', error);
	}
};

const deleteLoginHistory = async (provider: string) => {
	try {
		const stored = await get('previousLoginMethod');
		if (!stored || stored === 'null' || stored === 'undefined' || stored === '') {
			console.info('No previous login method found to delete');
			return;
		}

		const parsed = JSON.parse(stored);
		if (parsed.provider === provider) {
			await set('previousLoginMethod', null);
		} else {
			console.warn(`No login history found for provider: ${provider}`);
		}
	} catch (error) {
		console.error('Error in deleteLoginHistory:', error);
	}
};

const detectPreviousLoginMethod = async () => {
	try {
		const stored = await get('previousLoginMethod');
		if (!stored || stored === 'null' || stored === 'undefined' || stored === '') {
			console.info('No previous login method found');
			return null;
		}

		const parsed = JSON.parse(stored);
		const { provider, timestamp } = parsed || {};

		if (!provider || !timestamp) {
			console.warn('detectPreviousLoginMethod: incomplete data', parsed);
			return null;
		}

		const parsedTimestamp = new Date(timestamp);
		if (isNaN(parsedTimestamp.getTime())) {
			console.warn('detectPreviousLoginMethod: Invalid timestamp', timestamp);
			return null;
		}

		return {
			provider,
			timestamp: parsedTimestamp.toISOString(),
		};
	} catch (error) {
		console.error('Error in detectPreviousLoginMethod:', error);
		return null;
	}
};

const useAuthHelpers = () => {
	return {
		handleNativeSocialLogin,
		handleWebSocialLogin,
		logLoginHistory,
		detectPreviousLoginMethod,
		deleteLoginHistory,
	};
};

export default useAuthHelpers;
